import React from 'react';
import PropTypes from 'prop-types';
import { CInputGroup, CInputGroupText, CFormInput } from '@coreui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';

/**
 * @brief A reusable input group component with optional icon
 * @component
 *
 * @details
 * This component creates a styled input field with an optional icon prefix.
 * It wraps CoreUI's CInputGroup and CFormInput components to provide a consistent
 * look and feel across the application.
 *
 * @param {Object} props - Component props
 * @param {string} [props.type='text'] - Input field type (e.g., 'text', 'password', 'email')
 * @param {string} props.placeholder - Placeholder text for the input field
 * @param {string} props.value - Current value of the input field
 * @param {Function} props.onChange - Callback function triggered on input change
 * @param {Object} [props.icon] - FontAwesome icon object to display before the input
 * @param {string} [props.className='mb-3'] - Additional CSS classes
 * @param {string} [props.autoComplete] - HTML autocomplete attribute
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {boolean} [props.required=false] - Whether the input is required
 * @param {string} [props.aria-label] - Accessibility label for the input
 *
 * @example
 * <InputGroup
 *   type="text"
 *   placeholder="Username"
 *   value={username}
 *   onChange={handleUsernameChange}
 *   icon={faUser}
 *   required
 * />
 */
const InputGroup = ({
  type = 'text',
  placeholder,
  value,
  onChange,
  icon,
  className = 'mb-3',
  autoComplete,
  disabled = false,
  required = false,
  'aria-label': ariaLabel,
  ...props
}) => {
  return (
    <CInputGroup className={className}>
      {icon && (
        <CInputGroupText>
          <FontAwesomeIcon icon={icon} />
        </CInputGroupText>
      )}
      <CFormInput
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel || placeholder}
        {...props}
      />
    </CInputGroup>
  );
};

InputGroup.propTypes = {
  /** Input type (text, password, email, etc.) */
  type: PropTypes.string,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Current value of the input */
  value: PropTypes.string.isRequired,
  /** Change handler function */
  onChange: PropTypes.func.isRequired,
  /** FontAwesome icon to display */
  icon: PropTypes.object,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** HTML autocomplete attribute */
  autoComplete: PropTypes.string,
  /** Whether the input is disabled */
  disabled: PropTypes.bool,
  /** Whether the input is required */
  required: PropTypes.bool,
  /** Aria label for accessibility */
  'aria-label': PropTypes.string
};

/**
 * @brief A specialized InputGroup component for email input fields
 * @component
 *
 * @details
 * Pre-configured InputGroup for email addresses with an envelope icon.
 * Uses type="email" and includes proper accessibility attributes.
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Current value of the email input
 * @param {Function} props.onChange - Callback function triggered on input change
 * @param {string} [props.className='mb-3'] - Additional CSS classes
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {boolean} [props.required=false] - Whether the input is required
 *
 * @example
 * <EmailInputGroup
 *   value={email}
 *   onChange={handleEmailChange}
 *   required
 * />
 */
const EmailInputGroup = ({ 
  value,
  onChange,
  className = 'mb-3',
  disabled = false,
  required = false,
  ...props
}) => {
  return (
    <InputGroup
      type="email"
      placeholder="Email"
      autoComplete="email"
      value={value}
      onChange={onChange}
      icon={faEnvelope}
      className={className}
      disabled={disabled}
      required={required}
      aria-label="Email Address"
      {...props}
    />
  );
};

EmailInputGroup.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool
};

/**
 * @brief A specialized InputGroup component for password input fields
 * @component
 *
 * @details
 * Pre-configured InputGroup for password fields with a lock icon.
 * Uses type="password" and includes proper security attributes.
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Current value of the password input
 * @param {Function} props.onChange - Callback function triggered on input change
 * @param {string} [props.placeholder='Password'] - Placeholder text for the password field
 * @param {string} [props.className='mb-3'] - Additional CSS classes
 * @param {string} [props.autoComplete='current-password'] - HTML autocomplete attribute
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {boolean} [props.required=false] - Whether the input is required
 *
 * @example
 * <PasswordInputGroup
 *   value={password}
 *   onChange={handlePasswordChange}
 *   placeholder="Enter your password"
 *   required
 * />
 */
const PasswordInputGroup = ({ 
  value, 
  onChange, 
  placeholder = "Password",
  className = 'mb-3',
  autoComplete = "current-password",
  disabled = false,
  required = false,
  ...props 
}) => {
  return (
    <InputGroup
      type="password"
      placeholder={placeholder}
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
      icon={faLock}
      className={className}
      disabled={disabled}
      required={required}
      aria-label={placeholder}
      {...props}
    />
  );
};

PasswordInputGroup.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  autoComplete: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool
};

export { EmailInputGroup, PasswordInputGroup };
export default InputGroup;